import { isPlatformServer } from '@angular/common';
import { computed, Inject, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '@gb-webapp/gb-app';
import type { io, Socket } from 'socket.io-client';
import { AuthService } from '../../core/services/auth/auth.service';
import { StorageKeys, StorageService } from '../../core/services/storage/storage.service';
import { Token } from '../../interfaces/token';

export interface SocketErrorResponse {
  message: string;
  code: number;
  name: string;
}

export interface SocketResponse {
  success: boolean;
  error?: SocketErrorResponse;
  data?: string;
  invoiceHash?: string;
  activationCode?: string;
  iccid?: string;
  transactionId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentWebSocketService {
  private readonly authService = inject(AuthService);

  protected storageService = inject(StorageService);
  private socket: Socket | null = null;

  // Signals for reactive state
  public readonly _subscriptionData = signal<SocketResponse | null>(null);
  private readonly _isCloseRequested = signal<boolean>(false);
  private readonly _isConnected = signal<boolean>(false);

  // Computed signals for components to use
  public readonly subscriptionData = computed(() => this._subscriptionData());
  public readonly isCloseRequested = computed(() => this._isCloseRequested());
  public readonly isConnected = computed(() => this._isConnected());

  protected platform = Inject(PLATFORM_ID);

  protected cachedIo: typeof io | null = null;

  /**
   * Connect to the payment WebSocket service
   */
  public async connect(): Promise<void> {
    if (isPlatformServer(this.platform)) {
      return;
    }

    this.socket?.disconnect();

    const tokenData = this.storageService.getKey<Token>(StorageKeys.Token);
    const token = tokenData?.token;

    const connectionConfig: {
      autoConnect: boolean;
      reconnection: boolean;
      query?: { token: string };
    } = {
      autoConnect: true,
      reconnection: true,
      ...(token && { query: { token } }),
    };

    const io = await this.getIo();
    this.socket = io(environment.paymentMsUrl, connectionConfig);
    this.setupSocketListeners();
  }

  public async getIo(): Promise<typeof io> {
    return (
      this.cachedIo ??
      import('socket.io-client').then(({ io }) => {
        this.cachedIo = io;
        return io;
      })
    );
  }

  /**
   * Disconnect from the WebSocket service
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this._isConnected.set(false);
    }
  }

  /**
   * Emit confirmPayment event
   */
  public confirmPayment(payload: { paymentId: string }): void {
    if (this.socket?.connected) {
      this.socket.emit('confirmPayment', payload);
    }
  }

  /**
   * Clear subscription data
   */
  public clearSubscriptionData(): void {
    this._subscriptionData.set(null);
  }

  /**
   * Reset close request flag
   */
  public resetCloseRequest(): void {
    this._isCloseRequested.set(false);
  }

  /**
   * Setup socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to payment WebSocket');
      this._isConnected.set(true);
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from payment WebSocket');
      this._isConnected.set(false);
    });

    this.socket.on('connect_error', (error: { message?: string }) => {
      console.error('Connection error:', error);
      this._isConnected.set(false);
    });

    // Business logic events
    this.socket.on('subscriptionData', (data: unknown) => {
      this._subscriptionData.set(data as SocketResponse);
    });

    this.socket.on('closeConnection', () => {
      console.log('Server requested to close the connection');
    });
  }
}
